import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { NostrCanvasProvider, useNostrCanvas } from '@soapbox.pub/nostr-canvas/react';
import { toast } from 'sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/sonner';
import { useNostrLogin } from '@/hooks/useNostrLogin';
import { useRelays } from '@/hooks/useRelays';
import { SignerProvider } from '@/hooks/useSignerContext';
import { StorageScopeProvider } from '@/hooks/useStorageScope';
import { SimpleAdapter } from '@/lib/adapter';
import { grantBackend } from '@/lib/grants';
import { AppRouter } from '@/AppRouter';

export default function App() {
  // Auth state is read from unscoped global keys — must come first to establish scope
  const login = useNostrLogin();

  return (
    <StorageScopeProvider pubkey={login.pubkey}>
      <AppInner login={login} />
    </StorageScopeProvider>
  );
}

/** Inner app — rendered within the storage scope so all hooks read from scoped keys. */
function AppInner({ login }: { login: ReturnType<typeof useNostrLogin> }) {
  const { relays, setRelays } = useRelays();

  // Keep login's relay ref in sync (ref assignment only, no state update)
  login.setRelays(relays);

  // Single adapter instance for the whole app
  const adapterRef = useRef<SimpleAdapter | null>(null);
  if (adapterRef.current == null) {
    adapterRef.current = new SimpleAdapter(relays);
  }
  // Keep relays and signer in sync
  useLayoutEffect(() => {
    adapterRef.current!.setRelays(relays);
    adapterRef.current!.setSigner(login.signer);
  });

  const runtimeOptions = useMemo(
    () => ({ grantBackend }),
    [],
  );

  // Snapshot the adapter for JSX — safe: ref is always initialized above
  // eslint-disable-next-line react-hooks/refs
  const adapter = adapterRef.current!;

  return (
    <TooltipProvider>
      <SignerProvider value={login.signer}>
        <NostrCanvasProvider adapter={adapter} options={runtimeOptions}>
          <RuntimeBridge pubkey={login.pubkey} />
          <AppRouter
            pubkey={login.pubkey}
            profile={login.profile}
            isLoading={login.isLoading}
            isExtensionAvailable={login.isExtensionAvailable}
            relays={relays}
            onLoginExtension={login.loginExtension}
            onLoginNsec={login.loginNsec}
            onLogout={login.logout}
            onSetRelays={setRelays}
            onRefreshProfile={login.refreshProfile}
          />
          <Toaster />
        </NostrCanvasProvider>
      </SignerProvider>
    </TooltipProvider>
  );
}

/**
 * Sits inside NostrCanvasProvider so it can access the runtime. Keeps the
 * runtime's identity scope in sync with auth state and forwards tile
 * notifications to Sonner.
 */
function RuntimeBridge({ pubkey }: { pubkey: string | null }) {
  const { runtime } = useNostrCanvas();

  // Keep the runtime's storage/identity scope in sync with auth state.
  useEffect(() => {
    if (!runtime) return;
    runtime.setScope(pubkey);
  }, [runtime, pubkey]);

  // Notification forwarding
  useEffect(() => {
    if (!runtime) return;
    return runtime.on('notify', ({ message, variant }) => {
      switch (variant) {
        case 'success': toast.success(message); break;
        case 'warning': toast.warning(message); break;
        case 'danger':  toast.error(message);   break;
        default:        toast(message);          break;
      }
    });
  }, [runtime]);

  return null;
}
